import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Heart, Package, Search, ShoppingCart, Users, X, ChevronLeft } from 'lucide-react'
import { ApiError, api } from '@/lib/api'
import { displayCurrencyLabel, formatPrice, formatRoomPrice, isLodgingShop, localizedName, stockLabel } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import { CatalogFilters } from '@/components/CatalogFilters'
import { RoomBookingModal } from '@/components/RoomBookingModal'
import { RoomDetailsModal } from '@/components/RoomDetailsModal'
import { parseAmenities } from '@/lib/amenities'
import type { Category, FilterFacets, LodgingRoomType, ProductSummary, Store, WishlistItem } from '@/shared/types'

interface PublicSettings {
  multi_store: boolean
  currency_rate: number
  shop_name?: string
  home_page_enabled?: boolean
}

function withoutValue(record: Record<string, string[]>, key: string, value: string): Record<string, string[]> {
  const next = (record[key] ?? []).filter((v) => v !== value)
  return { ...record, [key]: next }
}

function withValue(record: Record<string, string[]>, key: string, value: string): Record<string, string[]> {
  return { ...record, [key]: [...(record[key] ?? []), value] }
}

// Drops spec names whose value list is empty, so the request body only
// carries active selections.
function compactRecord(record: Record<string, string[]>): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const [key, values] of Object.entries(record)) {
    if (values.length > 0) result[key] = values
  }
  return result
}

function includeFromUrl(): Record<string, string[]> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const attr = params.get('attr')?.trim()
  const value = params.get('value')?.trim()
  if (!attr || !value) return {}
  return { [attr]: [value] }
}

type CategoryControlsVariant = 'mobile' | 'desktop'

export function CatalogPage() {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const T = useT('catalog')
  const qc = useQueryClient()
  const navigate = useNavigate()
  const routeSearch = useSearch({ from: '/catalog' })

  const [search, setSearch] = useState(routeSearch.q ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(routeSearch.q ?? '')
  const [activeStore, setActiveStore] = useState<number | null>(null)
  const [activeCat, setActiveCat] = useState<number | null>(routeSearch.category ?? null)

  const [include, setInclude] = useState<Record<string, string[]>>(() => includeFromUrl())
  const [exclude, setExclude] = useState<Record<string, string[]>>({})
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [variantName, setVariantName] = useState('')

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 400)
    return () => clearTimeout(handler)
  }, [search])

  // Sync router search params with filters state
  useEffect(() => {
    if (routeSearch.attr && routeSearch.value) {
      setInclude({ [routeSearch.attr]: [routeSearch.value] }) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [routeSearch.attr, routeSearch.value])

  useEffect(() => {
    if (routeSearch.category !== undefined) {
      setActiveCat(routeSearch.category) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [routeSearch.category])

  useEffect(() => {
    if (routeSearch.q !== undefined) {
      setSearch(routeSearch.q) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [routeSearch.q])

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })
  const multiStore = Boolean(settings?.multi_store)
  const currencyRate = settings?.currency_rate || 1
  const isLodging = isLodgingShop(settings?.shop_name)
  const currencyLabel = displayCurrencyLabel(settings?.shop_name)
  const priceRangeLabel = T.filters.priceRangeWithCurrency
    ? T.filters.priceRangeWithCurrency(currencyLabel)
    : T.filters.priceRange

  const [selectedRoom, setSelectedRoom] = useState<LodgingRoomType | null>(null)
  const [selectedDetailsRoom, setSelectedDetailsRoom] = useState<LodgingRoomType | null>(null)

  // Lodging shops have no `products` — their catalog is room types,
  // booked instead of added to cart. Folded into the same grid rather
  // than a separate page so the browsing UX matches every other shop.
  const { data: roomTypes } = useQuery<LodgingRoomType[]>({
    queryKey: ['lodging-room-types', 1],
    queryFn: () => api.get<LodgingRoomType[]>('/lodging/room-types?store_id=1'),
    enabled: isLodging,
  })

  const catalogTitle = settings?.shop_name
    ? (T.fragranceCatalogShop ? T.fragranceCatalogShop(settings.shop_name) : (lang === 'en' ? `Fragrance Catalog — ${settings.shop_name}` : lang === 'ru' ? `Каталог ароматов — ${settings.shop_name}` : `Каталог ароматів — ${settings.shop_name}`))
    : (T.fragranceCatalog || (lang === 'en' ? 'Fragrance Catalog' : lang === 'ru' ? 'Каталог ароматов' : 'Каталог ароматів'))
  usePageTitle(catalogTitle)

  const { data: stores } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => api.get<Store[]>('/stores'),
    enabled: multiStore,
  })

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories', activeStore],
    queryFn: () => api.get<Category[]>(activeStore !== null ? `/categories?store_id=${activeStore}` : '/categories'),
  })

  // Attribute + price filters (facets fetched once, applied via POST
  // /products/filter). This is additive on top of the existing category /
  // store view, not a replacement of it: when an attribute/price filter is
  // active, the grid switches to the filter endpoint's results and the
  // category/store dropdowns are set aside rather than combined with it.
  // Reconciling both server-side (category/store scoped attribute search)
  // is left for a follow-up iteration.
  const { data: facets } = useQuery<FilterFacets>({
    queryKey: ['product-filter-facets'],
    queryFn: () => api.get<FilterFacets>('/products/filter-facets'),
    staleTime: 300_000,
  })

  // Facets carry USD prices (the storefront's base currency) but the
  // price inputs are UAH, matching what customers see on every product
  // card — convert both directions at this boundary so filtering stays
  // in USD server-side while the UI stays in UAH.
  const facetsUah: FilterFacets | undefined = facets && {
    ...facets,
    price_min: Math.round(facets.price_min * currencyRate),
    price_max: Math.round(facets.price_max * currencyRate),
  }

  const hasActiveFilters =
    Object.values(include).some((values) => values.length > 0) ||
    Object.values(exclude).some((values) => values.length > 0) ||
    priceMin.trim() !== '' ||
    priceMax.trim() !== '' ||
    debouncedSearch.trim() !== ''

  const activeFilterCount =
    Object.values(include).reduce((sum, values) => sum + values.length, 0) +
    Object.values(exclude).reduce((sum, values) => sum + values.length, 0) +
    (priceMin.trim() !== '' ? 1 : 0) +
    (priceMax.trim() !== '' ? 1 : 0) +
    (debouncedSearch.trim() !== '' ? 1 : 0)

  const cycleChip = (specName: string, option: string) => {
    haptic('light')
    const state = include[specName]?.includes(option)
      ? 'include'
      : exclude[specName]?.includes(option)
        ? 'exclude'
        : 'neutral'
    if (state === 'neutral') {
      setInclude((prev) => withValue(prev, specName, option))
    } else if (state === 'include') {
      setInclude((prev) => withoutValue(prev, specName, option))
      setExclude((prev) => withValue(prev, specName, option))
    } else {
      setExclude((prev) => withoutValue(prev, specName, option))
    }
  }

  const removeFilterValue = (specName: string, option: string) => {
    haptic('light')
    setInclude((prev) => withoutValue(prev, specName, option))
    setExclude((prev) => withoutValue(prev, specName, option))
  }

  const clearFilters = () => {
    haptic('light')
    setInclude({})
    setExclude({})
    setPriceMin('')
    setPriceMax('')
    setVariantName('')
    setSearch('')
  }

  const { data: baseProducts, isLoading: baseLoading } = useQuery<ProductSummary[]>({
    queryKey: ['products', activeStore, activeCat],
    queryFn: () => {
      const params = new URLSearchParams()
      if (activeStore !== null) params.set('store_id', String(activeStore))
      if (activeCat !== null) params.set('category_id', String(activeCat))
      const query = params.toString()
      return api.get<ProductSummary[]>(query ? `/products?${query}` : '/products')
    },
    enabled: !hasActiveFilters,
  })

  const { data: filteredProducts, isLoading: filterLoading } = useQuery<ProductSummary[]>({
    queryKey: ['products-filter', activeStore, activeCat, include, exclude, priceMin, priceMax, variantName, debouncedSearch],
    queryFn: () =>
      api.post<ProductSummary[]>('/products/filter', {
        category_id: activeCat ?? null,
        store_id: activeStore ?? null,
        include: compactRecord(include),
        exclude: compactRecord(exclude),
        price_min: priceMin.trim() !== '' ? Number(priceMin) / currencyRate : null,
        price_max: priceMax.trim() !== '' ? Number(priceMax) / currencyRate : null,
        variant_name: variantName || null,
        q: debouncedSearch.trim() || null,
      }),
    enabled: hasActiveFilters,
  })

  const products = hasActiveFilters ? filteredProducts : baseProducts
  const isLoading = hasActiveFilters ? filterLoading : baseLoading

  const { data: wishlist } = useQuery<WishlistItem[]>({
    queryKey: ['wishlist'],
    queryFn: () => api.get<WishlistItem[]>('/wishlist'),
  })

  const addMutation = useMutation<{ new_qty: number }, ApiError, number>({
    mutationFn: (productId) =>
      api.post<{ new_qty: number }>('/cart/add', {
        product_id: productId,
        qty: 1,
        store_id: activeStore,
      }),
    onSuccess: async () => {
      haptic('success')
      const cart = await api.get<{ count: number }>('/cart')
      setCartCount(cart.count)
      qc.invalidateQueries({ queryKey: ['cart'] })
    },
    onError: (err) => {
      haptic('error')
      // Anonymous visitors can browse the public catalog, but adding to
      // cart needs an identity — send them to log in instead of showing
      // a confusing generic failure.
      if (err instanceof ApiError && err.status === 401) {
        void navigate({ to: '/login' })
        return
      }
      alert(T.addFail)
    },
  })

  const wishlistMutation = useMutation<{ added: boolean }, ApiError, number>({
    mutationFn: (productId) => api.post<{ added: boolean }>(`/wishlist/${productId}`, {}),
    onSuccess: () => {
      haptic('light')
      qc.invalidateQueries({ queryKey: ['wishlist'] })
    },
    onError: (err) => {
      haptic('error')
      if (err instanceof ApiError && err.status === 401) {
        void navigate({ to: '/login' })
      }
    },
  })

  const wishlistIds = new Set((wishlist ?? []).map((item) => item.product_id))

  const filtered = (products ?? []).filter((product) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return product.name_uk.toLowerCase().includes(q) || (product.name_en ?? '').toLowerCase().includes(q)
  })
  const showResultCount = !isLoading && (hasActiveFilters || search.trim() !== '')
  const resultCountLabel =
    typeof T.resultCount === 'function' ? T.resultCount(filtered.length) : String(filtered.length)

  const shopName = (settings?.shop_name ?? '').toLowerCase().replace(/\s+/g, '')
  const isMayParfum = shopName.includes('mayparfum')
  const isKlodchik = shopName.includes('klodchik') || shopName.includes('заточ') || shopName.includes('sharp')
  const isDarkTheme = isMayParfum || isKlodchik

  const renderCategoryControls = (variant: CategoryControlsVariant) => (
    <>
      <button
        onClick={() => setActiveCat(null)}
        className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium md:w-full md:rounded-xl md:px-3 md:py-2 md:text-left transition ${
          activeCat === null
            ? isDarkTheme
              ? 'border-transparent bg-[#c9a24b] text-[#17120f] font-semibold shadow-md shadow-[#c9a24b]/10'
              : 'border-brand-600 bg-brand-600 text-white'
            : isDarkTheme
              ? 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-[#c9a24b]'
              : 'border-gray-300 text-gray-600 hover:text-gray-900 bg-white'
        }`}
      >
        {T.all}
      </button>
      {(categories ?? []).map((category) => (
        <button
          key={`${variant}:${category.id}`}
          onClick={() => setActiveCat(category.id === activeCat ? null : category.id)}
          className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium md:w-full md:rounded-xl md:px-3 md:py-2 md:text-left transition ${
            activeCat === category.id
              ? isDarkTheme
                ? 'border-transparent bg-[#c9a24b] text-[#17120f] font-semibold shadow-md shadow-[#c9a24b]/10'
                : 'border-brand-600 bg-brand-600 text-white'
              : isDarkTheme
                ? 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-[#c9a24b]'
                : 'border-gray-300 text-gray-600 hover:text-gray-900 bg-white'
          }`}
        >
          {localizedName(lang, category.name_uk, category.name_en)}
        </button>
      ))}
    </>
  )

  return (
    <div className={`min-h-screen pb-24 ${
      isMayParfum
        ? 'bg-[#3a4352] text-[#f8f3ea]'
        : isKlodchik
          ? 'bg-[#15181c] text-slate-100'
          : 'bg-[#fbfaf7] md:bg-[#f5f2ec] text-gray-950'
    }`}>
      <div className="mx-auto max-w-6xl px-4 pb-4 pt-4 md:px-6 md:pb-5 md:pt-6 xl:max-w-7xl 2xl:max-w-[1400px]">
        <div className="flex items-center gap-3 pb-3 justify-between">
          <div className="flex items-center gap-2">
            {settings?.home_page_enabled && (
              <Link
                to="/"
                className={`mr-1 rounded-full p-1.5 transition ${
                  isDarkTheme ? 'text-slate-200 hover:bg-[#1f242d] hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </Link>
            )}
            <h1 className={`text-lg font-bold md:text-3xl ${isDarkTheme ? 'text-white' : 'text-gray-950'}`}>{T.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {facetsUah && (
              <CatalogFilters
                facets={facetsUah}
                filterLabel={T.filterBtn}
                T={T.filters}
                include={include}
                exclude={exclude}
                onCycle={cycleChip}
                onRemove={removeFilterValue}
                priceMin={priceMin}
                priceMax={priceMax}
                onPriceMinChange={setPriceMin}
                onPriceMaxChange={setPriceMax}
                variantName={variantName}
                onVariantNameChange={setVariantName}
                activeCount={activeFilterCount}
                onClear={clearFilters}
                isDarkTheme={isDarkTheme}
                currencyLabel={currencyLabel}
              />
            )}
            <Link
              to="/wishlist"
              className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium backdrop-blur-sm transition ${
                isDarkTheme
                  ? 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-[#c9a24b]'
                  : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900'
              }`}
            >
              <Heart className="h-3.5 w-3.5" />
              {T.wishlist}
              {(wishlist?.length ?? 0) > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  isDarkTheme ? 'bg-[#c9a24b] text-[#17120f]' : 'bg-brand-600 text-white'
                }`}>
                  {wishlist!.length}
                </span>
              )}
            </Link>
          </div>
        </div>

        <div className="relative mb-3 md:max-w-2xl">
          <Search className={`absolute left-3.5 top-3.5 h-4 w-4 ${isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={T.search}
            className={`w-full rounded-2xl border py-2.5 pl-10 pr-9 text-sm focus:outline-none transition ${
              isDarkTheme
                ? 'border-[#444e5d] bg-[#1f242d] text-slate-100 placeholder-slate-400 focus:border-[#c9a24b]'
                : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500'
            }`}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className={`absolute right-3 top-3 rounded-full p-0.5 ${
                isDarkTheme ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {multiStore && (stores?.length ?? 0) > 0 && (
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => {
                setActiveStore(null)
                setActiveCat(null)
              }}
              className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                activeStore === null
                  ? isDarkTheme
                    ? 'border-transparent bg-[#c9a24b] text-[#17120f] font-semibold'
                    : 'border-brand-600 bg-brand-600 text-white'
                  : isDarkTheme
                    ? 'border-[#444e5d] bg-[#1f242d] text-slate-200'
                    : 'border-gray-300 text-gray-600'
              }`}
            >
              {T.allStores}
            </button>
            {stores!.map((store) => (
              <button
                key={store.id}
                onClick={() => {
                  setActiveStore(store.id === activeStore ? null : store.id)
                  setActiveCat(null)
                }}
                className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                  activeStore === store.id
                    ? isDarkTheme
                      ? 'border-transparent bg-[#c9a24b] text-[#17120f] font-semibold'
                      : 'border-brand-600 bg-brand-600 text-white'
                    : isDarkTheme
                      ? 'border-[#444e5d] bg-[#1f242d] text-slate-200'
                      : 'border-gray-300 text-gray-600'
                }`}
              >
                {localizedName(lang, store.name_uk, store.name_en)}
              </button>
            ))}
          </div>
        )}

        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 md:hidden">
          {renderCategoryControls('mobile')}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mx-4 mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-[#444e5d]/60 bg-[#1f242d] p-3 text-xs md:mx-auto md:max-w-6xl xl:max-w-7xl 2xl:max-w-[1400px]">
          <div className="flex items-center gap-2 text-slate-300">
            <span className="font-semibold">{T.filters.selected}</span>
            <button onClick={clearFilters} className={`font-semibold underline ${isDarkTheme ? 'text-[#c9a24b]' : 'text-brand-600'}`}>
              {T.filters.clear}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(include).flatMap(([specName, options]) =>
              options.map((option) => (
                <button
                  key={`inc:${specName}:${option}`}
                  onClick={() => removeFilterValue(specName, option)}
                  className="flex items-center gap-1 rounded-full border border-green-600 bg-green-600 px-2 py-1 text-white"
                >
                  {specName}: {option}
                  <X className="h-3 w-3" />
                </button>
              )),
            )}
            {Object.entries(exclude).flatMap(([specName, options]) =>
              options.map((option) => (
                <button
                  key={`exc:${specName}:${option}`}
                  onClick={() => removeFilterValue(specName, option)}
                  className="flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-1 text-red-500 line-through opacity-60"
                  disabled
                >
                  {specName}: {option}
                  <X className="h-3 w-3" />
                </button>
              )),
            )}
            {(priceMin.trim() !== '' || priceMax.trim() !== '') && (
              <button
                onClick={() => {
                  haptic('light')
                  setPriceMin('')
                  setPriceMax('')
                }}
                className={`flex items-center gap-1 rounded-full border px-2 py-1 ${
                  isDarkTheme
                    ? 'border-[#444e5d] bg-[#28303c] text-slate-200'
                    : 'border-brand-300 bg-white text-brand-700'
                }`}
              >
                {priceRangeLabel}: {priceMin || '…'}–{priceMax || '…'}
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {showResultCount && (
        <div className={`mx-4 mb-3 flex items-center justify-between rounded-xl border px-3 py-2 text-xs shadow-sm md:mx-auto md:max-w-6xl xl:max-w-7xl 2xl:max-w-[1400px] ${
          isMayParfum
            ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]'
            : isKlodchik
              ? 'border-slate-800 bg-[#1e2329] text-slate-200'
              : 'border-gray-100 bg-white text-gray-500'
        }`}>
          <span className={`font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-gray-800'}`}>{resultCountLabel}</span>
          {hasActiveFilters && (
            <button onClick={clearFilters} className={`font-semibold ${isDarkTheme ? 'text-[#c9a24b]' : 'text-brand-600'}`}>
              {T.filters.clear}
            </button>
          )}
        </div>
      )}

      <div className={`mx-auto max-w-6xl md:px-6 xl:max-w-7xl 2xl:max-w-[1400px] ${
        (categories?.length ?? 0) > 0
          ? 'md:grid md:grid-cols-[220px_1fr] md:gap-5 xl:grid-cols-[260px_1fr] xl:gap-6'
          : ''
      }`}>
        {(categories?.length ?? 0) > 0 && (
          <aside className={`sticky top-4 hidden h-fit rounded-2xl border p-4 shadow-md md:block ${
            isMayParfum
              ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]'
              : isKlodchik
                ? 'border-slate-800 bg-[#1e2329] text-slate-100'
                : 'border-gray-200 bg-white'
          }`}>
            <p className={`mb-3 text-xs font-semibold uppercase tracking-wider ${isDarkTheme ? 'text-slate-400' : 'text-gray-900'}`}>
              {T.categoriesTitle}
            </p>
            <div className="flex flex-col gap-2">{renderCategoryControls('desktop')}</div>
          </aside>
        )}

        <div>
          {isLodging && Boolean(roomTypes?.length) && (
            <ul className="mb-5 space-y-4 px-4 md:px-0">
              {roomTypes?.map((rt) => {
                const name = localizedName(lang, rt.name_uk, rt.name_en, rt.name_ru)
                const roomT = {
                  fromPrice: T.fromPrice || (lang === 'en' ? 'from' : lang === 'ru' ? 'от' : 'від'),
                  perNight: T.perNight || (lang === 'en' ? '/ night' : lang === 'ru' ? '/ ночь' : '/ ніч'),
                  book: T.book || (lang === 'en' ? 'Book' : lang === 'ru' ? 'Забронировать' : 'Забронювати'),
                  details: T.details || (lang === 'en' ? 'Details' : lang === 'ru' ? 'Характеристики' : 'Характеристики'),
                }
                const roomPrice = formatRoomPrice(rt.base_price_uah)
                const roomAmenities = parseAmenities(rt.amenities).slice(0, 4)
                return (
                  <li key={`room:${rt.id}`} className={`overflow-hidden rounded-2xl border shadow-md transition-all hover:border-[#c9a24b] sm:flex ${
                    isMayParfum
                      ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]'
                      : isKlodchik
                        ? 'border-slate-800 bg-[#1e2329] text-slate-100'
                        : 'border-gray-100 bg-white text-gray-900'
                  }`}>
                    {rt.photo_url ? (
                      <img src={rt.photo_url} alt={name} className="h-56 w-full object-cover sm:h-auto sm:w-2/5 sm:shrink-0 lg:w-1/2" />
                    ) : (
                      <div className={`flex h-56 w-full items-center justify-center sm:h-auto sm:w-2/5 sm:shrink-0 lg:w-1/2 ${isDarkTheme ? 'bg-[#1f242d]' : 'bg-gray-50'}`}>
                        <Package className={`h-10 w-10 ${isDarkTheme ? 'text-slate-500' : 'text-gray-300'}`} />
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col justify-center p-4 sm:p-6">
                      <p className={`text-lg font-semibold leading-tight md:text-xl ${
                        isDarkTheme ? 'text-slate-100' : 'text-gray-900'
                      }`}>{name}</p>
                      <p className={`mt-2 flex items-center gap-1 text-sm ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>
                        <Users className="h-4 w-4" /> {rt.capacity}
                      </p>
                      {roomAmenities.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {roomAmenities.map((a) => (
                            <span
                              key={a.code}
                              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                                isDarkTheme ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'
                              }`}
                            >
                              <a.icon className="h-3 w-3" />
                              {a.label[lang]}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className={`mt-3 text-lg font-bold md:text-xl ${isDarkTheme ? 'text-[#c9a24b]' : 'text-brand-600'}`}>
                        {roomT.fromPrice} {roomPrice} {roomT.perNight}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => { haptic('light'); setSelectedDetailsRoom(rt) }}
                          className={`flex items-center justify-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
                            isDarkTheme
                              ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                              : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {roomT.details}
                        </button>
                        <button
                          onClick={() => { haptic('medium'); setSelectedRoom(rt) }}
                          className={`flex items-center justify-center gap-1.5 rounded-xl px-6 py-2.5 text-sm font-semibold transition-all ${
                            isDarkTheme
                              ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d4af37] active:scale-[0.98]'
                              : 'bg-brand-600 text-white hover:bg-brand-700'
                          }`}
                        >
                          {roomT.book}
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {isLoading ? (
            <p className="p-4 text-slate-400">{T.loading}</p>
          ) : filtered.length === 0 ? (
            !roomTypes?.length && (
              <div className="flex flex-col items-center justify-center p-12 text-slate-400">
                <Package className="mb-3 h-12 w-12 opacity-40" />
                <p>{T.empty}</p>
              </div>
            )
          ) : (
            <ul className="grid grid-cols-2 gap-3 px-4 pb-20 md:grid-cols-3 md:px-0 lg:grid-cols-4 xl:grid-cols-5 xl:gap-4">
              {filtered.map((product) => {
                const name = localizedName(lang, product.name_uk, product.name_en)
                const canAdd = product.in_stock || product.is_made_to_order
                const isAdding = addMutation.isPending && addMutation.variables === product.id
                const wished = wishlistIds.has(product.id)
                return (
                  <li key={product.id} className={`min-w-0 overflow-hidden rounded-2xl border shadow-md transition-all hover:border-[#c9a24b] ${
                    isMayParfum
                      ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]'
                      : isKlodchik
                        ? 'border-slate-800 bg-[#1e2329] text-slate-100'
                        : 'border-gray-100 bg-white text-gray-900'
                  }`}>
                    <Link to="/catalog/$productSlug" params={{ productSlug: product.slug || String(product.id) }} className="relative block pb-[100%]">
                      {product.photo_url ? (
                        <img src={product.photo_url} alt={name} className="absolute inset-0 h-full w-full object-cover transition hover:scale-[1.03]" />
                      ) : (
                        <div className={`absolute inset-0 flex items-center justify-center ${isDarkTheme ? 'bg-[#1f242d]' : 'bg-gray-50'}`}>
                          <Package className={`h-10 w-10 ${isDarkTheme ? 'text-slate-500' : 'text-gray-300'}`} />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          haptic('light')
                          wishlistMutation.mutate(product.id)
                        }}
                        className={`absolute right-2 top-2 rounded-full p-2 shadow-md backdrop-blur-sm ${
                          isDarkTheme
                            ? 'bg-slate-900/80 text-slate-400 hover:text-slate-200'
                            : 'bg-white/90 text-gray-500'
                        } ${wished ? 'text-rose-500' : ''}`}
                      >
                        <Heart className={`h-4 w-4 ${wished ? 'fill-current text-rose-500' : ''}`} />
                      </button>
                    </Link>

                    <div className="p-3">
                      <Link to="/catalog/$productSlug" params={{ productSlug: product.slug || String(product.id) }} className="block">
                        <p className={`line-clamp-2 text-sm font-semibold leading-snug ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                          {name}
                        </p>
                      </Link>

                      <div className="mt-2 flex items-baseline justify-between gap-1">
                        <p className={`text-base font-bold ${isDarkTheme ? 'text-[#c9a24b]' : 'text-brand-600'}`}>
                          {formatPrice(product.price_uah)}
                        </p>
                        <span className={`text-[10px] ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>
                          {stockLabel(lang, product.stock_qty ?? 0)}
                        </span>
                      </div>

                      <button
                        type="button"
                        disabled={!canAdd || isAdding}
                        onClick={() => addMutation.mutate(product.id)}
                        className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition ${
                          !canAdd
                            ? 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'
                            : isDarkTheme
                              ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d4af37]'
                              : 'bg-brand-600 text-white hover:bg-brand-700'
                        }`}
                      >
                        {!canAdd ? T.outOfStock : isAdding ? T.adding : <><ShoppingCart className="h-3.5 w-3.5" /> {T.addToCart}</>}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {selectedDetailsRoom && (
        <RoomDetailsModal
          roomType={selectedDetailsRoom}
          onClose={() => setSelectedDetailsRoom(null)}
          onBook={() => {
            const rt = selectedDetailsRoom
            setSelectedDetailsRoom(null)
            setSelectedRoom(rt)
          }}
        />
      )}

      {selectedRoom && (
        <RoomBookingModal roomType={selectedRoom} onClose={() => setSelectedRoom(null)} />
      )}
    </div>
  )
}
